'use client';

import React, { useRef, useEffect } from "react";
import PrimaryButton from "@/components/ui/PrimaryButton/PrimaryButton";
import { LanguageType, LanguageDirection } from "@/lib/types";
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from "@gsap/react";
import { useAnimatedNavigate } from "@/hooks/useAnimatedNavigation";
import { usePathname } from "next/navigation";
import MuxPlayer from "@mux/mux-player-react";
import { useLenis } from "@/contexts/LenisContext";
import { useLoading } from "@/contexts/LoadingContext";
import Image from "next/image";
import { debug } from '@/lib/debug';
import { useIsLg, useIsTb } from '@/contexts/BreakpointContext';
gsap.registerPlugin(useGSAP, ScrollTrigger);

type TitleFont = "small" | "big";

interface HeroSectionProps {
  title: string
  paragraph?: string
  imageSrc: string
  imageAlt: string
  videoSrc?: string
  mediaType?: 'image' | 'video'
  buttonText?: string
  hint?: string
  language?: LanguageType
  titleFont: TitleFont
  useMuxPlayer?: boolean
  muxPlaybackIdDesktop?: string
  muxPlaybackIdMobile?: string
}


const LANGUAGE_TO_DIRECTION: Record<LanguageType, LanguageDirection> = {
  english: "ltr",
  arabic: "rtl"
}

const HeroSection: React.FC<HeroSectionProps> = ({
  title,
  paragraph,
  hint,
  language = "english",
  imageSrc,
  videoSrc,
  mediaType = "image",
  buttonText,
  titleFont,
  imageAlt,
  useMuxPlayer = false,
  muxPlaybackIdDesktop,
  muxPlaybackIdMobile,
}) => {
  const { lenis } = useLenis();
  const navigate = useAnimatedNavigate();
  const pathname = usePathname();
  const isLg = useIsLg();
  const isTb = useIsTb();
  const { setMuxPlayerReady } = useLoading();
  const containerRef = useRef<HTMLDivElement>(null);
  const sectionRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const secondaryContentRef = useRef<HTMLDivElement>(null);
  const heroImageRef = useRef<HTMLImageElement>(null);
  const titleCharsRef = useRef<HTMLSpanElement[]>([]);
  const titleWordsRef = useRef<HTMLSpanElement[]>([]);
  const videoRef = useRef<HTMLVideoElement>(null);
  const muxPlayerRef = useRef<HTMLDivElement>(null);
  const muxPlayerElementRef = useRef<any>(null);

  useEffect(() => {
    if (!useMuxPlayer || !muxPlayerElementRef.current || pathname !== '/') return;

    const muxPlayer = muxPlayerElementRef.current;

    // Defined inside the effect so we capture a stable reference for the
    // matching removeEventListener calls in cleanup. Previously this lived
    // on the component body — recreated every render — which made the
    // listener refs stale and impossible to remove.
    const handleReady = () => {
      debug.log('[HeroSection] MuxPlayer video ready, setting MuxPlayerReady to true');
      if (pathname === '/') {
        setMuxPlayerReady(true);
      }
    };

    const getVideoElement = (): HTMLVideoElement | null => {
      if (muxPlayer.media) return muxPlayer.media;

      if (muxPlayer.shadowRoot) {
        const video = muxPlayer.shadowRoot.querySelector('video');
        if (video) return video;
      }

      if (muxPlayerRef.current) {
        const video = muxPlayerRef.current.querySelector('video');
        if (video) return video;
      }

      return null;
    };

    // Track which video element we attached listeners to so cleanup can
    // remove them. Without this, an effect re-run (deps change) or unmount
    // before `loadedmetadata`/`canplay` fires leaves dangling listeners.
    let attachedVideo: HTMLVideoElement | null = null;
    const attach = (videoElement: HTMLVideoElement) => {
      if (attachedVideo) return;
      attachedVideo = videoElement;

      if ('disablePictureInPicture' in videoElement) {
        (videoElement as any).disablePictureInPicture = true;
      }

      videoElement.addEventListener('loadedmetadata', handleReady, { once: true });
      videoElement.addEventListener('canplay', handleReady, { once: true });

      if (videoElement.readyState >= 2) {
        handleReady();
      }
    };

    const checkInterval = setInterval(() => {
      const videoElement = getVideoElement();
      if (videoElement) {
        clearInterval(checkInterval);
        attach(videoElement);
      }
    }, 100);

    const videoElement = getVideoElement();
    if (videoElement) {
      clearInterval(checkInterval);
      attach(videoElement);
    }

    // Timeout fallback - if no MuxPlayer is present or it fails to load, mark as ready after 30s
    const timeout = setTimeout(() => {
      debug.log('[HeroSection] MuxPlayer timeout (30s), marking as ready');
      setMuxPlayerReady(true);
    }, 30000);

    return () => {
      clearInterval(checkInterval);
      clearTimeout(timeout);
      if (attachedVideo) {
        attachedVideo.removeEventListener('loadedmetadata', handleReady);
        attachedVideo.removeEventListener('canplay', handleReady);
      }
    };
  }, [useMuxPlayer, isLg, muxPlaybackIdDesktop, muxPlaybackIdMobile, pathname, setMuxPlayerReady]);

  // Seek to frame 0 and play when the transition is about to complete
  useEffect(() => {
    if (!useMuxPlayer || pathname !== '/') return;

    const handleVideoPrepare = () => {
      const muxPlayer = muxPlayerElementRef.current;
      if (!muxPlayer) return;

      const video = muxPlayer.media
        || muxPlayer.shadowRoot?.querySelector('video')
        || muxPlayerRef.current?.querySelector('video');

      if (video) {
        video.currentTime = 0;
        video.play().catch(() => { });
      }
    };

    window.addEventListener('heroVideoPrepare', handleVideoPrepare);
    return () => window.removeEventListener('heroVideoPrepare', handleVideoPrepare);
  }, [useMuxPlayer, pathname]);

  useGSAP(() => {
    const container = containerRef.current;
    const section = sectionRef.current;
    const content = contentRef.current;
    const titleChars = titleCharsRef.current;
    const titleWords = titleWordsRef.current;
    const secondaryContent = secondaryContentRef.current;
    const heroImage = heroImageRef.current;
    const heroVideo = videoRef.current;
    const muxPlayer = muxPlayerRef.current;
    const isArabic = language === 'arabic';
    if (!container || !section || !content) return;
    if (!isArabic && !titleChars) return;
    if (isArabic && !titleWords) return;
    if (!useMuxPlayer && mediaType === 'image' && !heroImage) return;
    if (!useMuxPlayer && mediaType === 'video' && !heroVideo) return;
    if (useMuxPlayer && !muxPlayer) return;

    if (isArabic) {
      gsap.set(titleWords, {
        autoAlpha: 0,
        filter: 'blur(100px)',
        y: -20,
        force3D: true,
      });
    } else {
      gsap.set(titleChars, {
        autoAlpha: 0,
        filter: 'blur(100px)',
        y: -20,
        force3D: true,
      });
    }

    if (!useMuxPlayer) {
      if (mediaType === 'image' && heroImage) {
        gsap.set(heroImage, {
          scale: isLg ? 1.1 : 1.15,
        });
      }

      if (mediaType === 'video' && heroVideo) {
        gsap.set(heroVideo, {
          scale: isLg ? 1.1 : 1.15,
        });
      }
    } else if (muxPlayer) {
      gsap.set(muxPlayer, {
        scale: isLg ? 1 : 1,
      });
    }

    if (secondaryContent) {
      gsap.set(secondaryContent, {
        visibility: 'hidden'
      });
    }

    const startAnimations = () => {
      if (useMuxPlayer && muxPlayer) {
        gsap.to(muxPlayer, {
          scale: 1,
          duration: 3.0,
          ease: "power4.out",
        });
      } else if (mediaType === 'image' && heroImage) {
        gsap.to(heroImage, {
          scale: 1,
          duration: 3.0,
          ease: "power4.out",
        });
      } else if (mediaType === 'video' && heroVideo) {
        gsap.to(heroVideo, {
          scale: 1,
          duration: 3.0,
          ease: "power4.out",
        });
      }

      if (isArabic) {
        gsap.to(titleWords, {
          autoAlpha: 1,
          filter: 'blur(0px)',
          y: 0,
          duration: 0.8,
          stagger: 0.1,
          ease: "power2.out",
          force3D: true,
        });
      } else {
        gsap.to(titleChars, {
          autoAlpha: 1,
          filter: 'blur(0px)',
          y: 0,
          duration: 0.8,
          stagger: 0.035,
          ease: "power2.out",
          force3D: true,
        });
      }

      const titleDuration = isArabic
        ? ((titleWords.length - 1) * 0.1) + 0.8 + 0.1
        : ((titleChars.length - 1) * 0.035) + 0.8 + 0.1;

      const mainTl = gsap.timeline();
      mainTl.add(() => {
        if (!secondaryContent) return;

        const downElements = secondaryContent.children;

        gsap.set(secondaryContent, {
          visibility: 'visible'
        });

        gsap.fromTo(downElements,
          {
            y: -50,
            autoAlpha: 0,
            force3D: true,
          },
          {
            y: 0,
            autoAlpha: 1,
            duration: 1,
            ease: "power2.out",
            stagger: 0.2,
            force3D: true,
          }
        );
      }, titleDuration - (0.5 + (Math.floor((isArabic ? titleWords.length : titleChars.length) / 10) * 0.15)));
    };

    let handleTransitionComplete: () => void;

    const path = window.location.pathname;
    if (path === '/legal') {
      startAnimations();
    } else {
      handleTransitionComplete = () => {
        startAnimations();
        window.removeEventListener('navigationTransitionComplete', handleTransitionComplete);
      };

      window.addEventListener('navigationTransitionComplete', handleTransitionComplete);
    }

    return () => {
      if (handleTransitionComplete) {
        window.removeEventListener('navigationTransitionComplete', handleTransitionComplete);
      }
    }

  }, [language]);

  useGSAP(() => {
    const container = containerRef.current;
    const section = sectionRef.current;
    if (!container || !section) return;

    // Reproduce the "expand to full-bleed on scroll" effect via transform: scale()
    // instead of animating `padding`. Animating padding on the section caused a
    // ~0.009 CLS hit because padding is a layout property — every scrub frame
    // resized the container. Scale is composited, so no layout shift.
    //
    // The scale factor depends on the current section / container dimensions,
    // which change on window resize. We use function-based values together
    // with `invalidateOnRefresh: true` so GSAP re-computes the target ratios
    // every time ScrollTrigger refreshes (ScrollTrigger auto-refreshes on
    // window.resize). We read `offsetWidth` / `offsetHeight` instead of
    // `getBoundingClientRect()` because the former report the layout box and
    // ignore any currently-applied transform — important here because on the
    // second refresh the container may still have scale(1.03) inline from the
    // previous tween, which would corrupt the new ratio.
    const getScaleX = () => {
      const sectionW = section.offsetWidth;
      const containerW = container.offsetWidth;
      return containerW > 0 ? sectionW / containerW : 1;
    };
    const getScaleY = () => {
      const sectionH = section.offsetHeight;
      const containerH = container.offsetHeight;
      return containerH > 0 ? sectionH / containerH : 1;
    };

    const scrollTl = gsap.timeline({
      scrollTrigger: {
        trigger: section,
        start: "top top",
        end: "+=20%",
        scrub: true,
        invalidateOnRefresh: true,
        // markers: true,
      }
    });

    scrollTl.to(container, {
      scaleX: getScaleX,
      scaleY: getScaleY,
      transformOrigin: 'center center',
      borderRadius: 0,
    });

  }, { dependencies: [isLg, isTb], revertOnUpdate: true });

  const titleWords = title.split(' ').map((word, wordIndex) =>
    wordIndex === 0 ? word : ` ${word}`
  );

  const isArabic = language === 'arabic';

  const titleCharacters = isArabic ? (
    titleWords.map((word, wordIndex) => (
      <span
        key={wordIndex}
        ref={el => {
          if (el) titleWordsRef.current[wordIndex] = el;
        }}
        style={{ display: 'inline-block', whiteSpace: 'nowrap' }}
      >
        {word}
      </span>
    ))
  ) : (
    titleWords.map((word, wordIndex) => (
      <div
        key={wordIndex}
        style={{ display: 'inline-block', whiteSpace: 'nowrap' }}
      >
        {word.split('').map((char, charIndex) => {
          const absoluteIndex = titleWords
            .slice(0, wordIndex)
            .reduce((acc, prevWord) => acc + prevWord.length, 0) + charIndex;

          return (
            <span
              key={`${wordIndex}-${charIndex}`}
              ref={el => {
                if (el) titleCharsRef.current[absoluteIndex] = el;
              }}
              style={{ display: 'inline-block', whiteSpace: 'pre' }}
            >
              {char}
            </span>
          );
        })}
      </div>
    ))
  );

  return (
    <section
      ref={sectionRef}
      className="
        hero-section
        padding-narrow-x-mobile
        padding-narrow-y-mobile
        padding-narrow-x-desktop
        padding-narrow-y-desktop
        relative
        w-full
        h-[100svh]
        overflow-hidden
        z-10
    ">
      <div
        ref={containerRef}
        className="
          relative
          w-full h-full
          overflow-hidden
          border-radius-main
          outline-secondary outline-stroke-soft-200
          px-[1.25rem]
          py-[2.5rem]
          lg:px-[2rem]
          lg:py-[3.5rem]
          flex items-center justify-center
        "
      >
        {useMuxPlayer && muxPlaybackIdDesktop && muxPlaybackIdMobile ? (
          <>
            <style>{`
              mux-player {
                --controls: none !important;
              }
              mux-player::part(controls) {
                display: none !important;
              }
              mux-player mux-controls {
                display: none !important;
              }
              mux-player button,
              mux-player [role="button"] {
                display: none !important;
              }
              mux-player video::-webkit-media-controls-picture-in-picture-button {
                display: none !important;
              }
              mux-player video {
                -webkit-appearance: none !important;
              }
              mux-player video::-moz-media-controls-picture-in-picture-button {
                display: none !important;
              }
              mux-player video::--moz-media-controls-picture-in-picture-button {
                display: none !important;
              }
            `}</style>
            <div
              ref={muxPlayerRef}
              className="absolute inset-0 w-[100vw] h-[100vh] -z-10 overflow-hidden"
              style={{ transformOrigin: 'center center' }}
            >
              <MuxPlayer
                ref={muxPlayerElementRef}
                streamType="on-demand"
                playbackId={isLg ? muxPlaybackIdDesktop : muxPlaybackIdMobile}
                autoPlay
                preload="auto"
                loop
                playsInline
                muted
                disablePictureInPicture
                style={{
                  width: '100%', height: '100%',
                  "--media-object-fit": "cover",
                  "--media-object-position": "center center"
                } as any}
              />
              <div
                className="absolute inset-0 w-full h-full pointer-events-none"
                style={{
                  backgroundColor: '#917D64',
                  mixBlendMode: 'multiply',
                  opacity: 0.8
                }}
              />
            </div>
          </>
        ) : mediaType === 'image' ? (
          <Image
            src={imageSrc}
            alt={imageAlt}
            fill
            sizes="100vw"
            priority
            fetchPriority="high"
            ref={heroImageRef}
            className="object-cover -z-10"
            style={{ transformOrigin: 'center center' }}
          />
        ) : (
          <video
            ref={videoRef}
            src={videoSrc}
            className="absolute inset-0 w-full h-full object-cover -z-10"
            style={{ transformOrigin: 'center center' }}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
          />
        )}

        <div className="h-full w-full flex flex-col items-center justify-end">
          <div ref={contentRef}
            className="
            w-[86vw]
            vsm:w-[86vw]
            tb:w-[88vw]
            lg:w-[94vw]
            ">
            <div className="
              w-full flex flex-col lg:flex-row
              lg:justify-between
              lg:flex-wrap-reverse
              gap-[1.5rem] lg:gap-x-[1rem] lg:gap-y-[2rem] lg:px-[2rem] ">
              {(paragraph || buttonText || hint) &&
                <div
                  ref={secondaryContentRef}
                  style={{
                    direction: LANGUAGE_TO_DIRECTION[language],
                    visibility: 'hidden'
                  }}
                  className="
                  order-2 lg:order-1 w-full lg:w-[29.8rem] lg:flex-grow tb:max-w-[37rem] flex flex-col gap-y-[1.25rem] lg:gap-y-[2rem] justify-end
                  ">
                  {paragraph &&
                    <p className={`
                    text-text-reversed
                    text-22--alexandria
                    text-balance supports-[text-wrap:pretty]:text-pretty
                  `}>
                      {paragraph}
                    </p>
                  }
                  {(buttonText || hint) &&
                    <div className=" lg:max-w-[30.6875rem] flex flex-row gap-[1rem] items-center justify-start ">
                      {buttonText &&
                        <div style={{ direction: "ltr" }}
                          className="w-fit"
                        >
                          <PrimaryButton
                            text={buttonText}
                            size={isLg ? 56 : 52}
                            type="accent"
                            language={language}
                            onClick={() => {
                              if (pathname === '/signup') {
                                const section = document.getElementById('signup-section');
                                if (section) {
                                  window.dispatchEvent(new CustomEvent('lenisScrollStart'));

                                  const y = section.getBoundingClientRect().top + window.scrollY;

                                  lenis?.scrollTo(y, {
                                    offset: 0,
                                    duration: 2,
                                    easing: (t: number) => t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1,
                                    immediate: false,
                                    lock: true,
                                    onComplete: () => {
                                      window.dispatchEvent(new CustomEvent('lenisScrollEnd'));
                                    }
                                  });
                                }
                              } else {
                                navigate('/signup');
                              }
                            }}
                          />
                        </div>
                      }
                      {hint &&
                        <p className={`
                          lg:w-[9rem]
                          lg:max-w-[13.5rem]
                          lg:flex-grow
                          text-text-reversed
                          text-16-to-12--alexandria
                        `}>
                          {hint}
                        </p>}
                    </div>
                  }
                </div>
              }

              <h1
                style={{ direction: LANGUAGE_TO_DIRECTION[language] }}
                className={`
                  order-1
                  lg:order-2
                  lg:min-w-[49.6875rem]
                  lg:w-[49.6875rem]
                  lg:flex-grow
                  lg:max-w-full
                  text-text-reversed
                  ${paragraph ? 'text-right' : 'text-center'}
                  ${titleFont === "small" ? 'text-120--neue-june' : 'text-192--neue-june'}
                `}>
                {titleCharacters}
              </h1>
            </div>

          </div>


        </div>
      </div>
    </section >
  )
};

export default HeroSection;
